import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Gift, Users, TrendingUp, Wallet, ShieldCheck, LogOut,
  Menu, X, Headphones, Sparkles, Megaphone, Trophy, Crown,
  MessageSquare, GitBranch, BarChart3, FileText, Bell, Activity,
} from "lucide-react";
import { useState, useEffect } from "react";
import { usePromoAuth } from "../context/AuthContext";
import { toast } from "sonner";
import NotificationBell from "./NotificationBell";
import api from "../lib/api";
import { AnnouncementPopup } from "../lib/realtime";

const NAV_GROUPS = [
  {
    label: null,
    items: [
      { to: "/promo/dashboard", label: "Dashboard", short: "Home", icon: LayoutDashboard },
    ],
  },
  {
    label: "Players",
    items: [
      { to: "/promo/referrals", label: "My Players", short: "Players", icon: Users },
      { to: "/promo/downline",  label: "Sub-Affiliates", short: "Downline", icon: GitBranch },
      { to: "/promo/funnel",    label: "Conversion Funnel", short: "Funnel", icon: BarChart3 },
      { to: "/promo/analytics", label: "Link Analytics", short: "Analytics", icon: Activity },
    ],
  },
  {
    label: "Earnings",
    items: [
      { to: "/promo/commissions", label: "Commissions", short: "Earnings", icon: TrendingUp },
      { to: "/promo/settlements", label: "Settlements", short: "Settlements", icon: FileText },
      { to: "/promo/forecast",    label: "Calendar & Forecast", short: "Forecast", icon: BarChart3 },
      { to: "/promo/payouts",     label: "Payouts",     short: "Payouts",  icon: Wallet },
    ],
  },
  {
    label: "Marketing",
    items: [
      { to: "/promo/promo-codes", label: "Promo Codes", short: "Codes", icon: Gift },
      { to: "/promo/campaigns",   label: "Campaigns",   short: "Campaigns", icon: Megaphone },
    ],
  },
  {
    label: "Compete",
    items: [
      { to: "/promo/leaderboard", label: "Leaderboard", short: "Leaderboard", icon: Trophy },
      { to: "/promo/contests",    label: "Contests",    short: "Contests", icon: Crown },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/promo/announcements", label: "Announcements", short: "Announce", icon: Bell },
      { to: "/promo/tickets",  label: "Support Tickets", short: "Tickets", icon: MessageSquare },
      { to: "/promo/security", label: "Security & 2FA", short: "Security", icon: ShieldCheck },
    ],
  },
];

export default function PromoLayout() {
  const { account, logout } = usePromoAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("You have been signed out");
    navigate("/promo/login", { replace: true });
  };

  return (
    <div className="min-h-screen promo-bg-main">
      {/* Top bar */}
      <header className="sticky top-0 z-30" style={{
        background: "linear-gradient(180deg, rgba(8,8,16,0.94), rgba(8,8,16,0.78))",
        backdropFilter: "blur(18px) saturate(140%)",
        WebkitBackdropFilter: "blur(18px) saturate(140%)",
        borderBottom: "1px solid rgba(34, 192, 115, 0.18)",
      }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 h-[58px] sm:h-[64px] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 rounded-lg p-btn-ghost shrink-0"
              aria-label="Toggle menu"
              data-testid="promo-mobile-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="p-brand-mark shrink-0" style={{ width: 36, height: 36, fontSize: 12 }}>M88</div>
              <div className="flex flex-col leading-tight min-w-0">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-white/45 font-semibold">Maxlive88</div>
                <div className="text-[13px] sm:text-[15px] font-black p-gold-text truncate">Affiliate Suite</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <NotificationBell />
            <div className="hidden lg:flex items-center gap-3 pl-3" style={{ borderLeft: "1px solid rgba(255,255,255,0.07)" }}>
              <div className="flex flex-col items-end leading-tight">
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 font-bold">Signed in</div>
                <div className="text-sm font-bold text-white">{account?.username}</div>
              </div>
            </div>
            <button onClick={handleLogout} className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[12px] font-semibold p-btn-ghost" data-testid="promo-logout">
              <LogOut size={13} /> Logout
            </button>
            <button onClick={handleLogout} className="sm:hidden p-2 rounded-lg p-btn-ghost" aria-label="Logout" data-testid="promo-logout-mobile">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 flex gap-6">
        {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />}

        <aside
          className={`${open ? "fixed top-0 left-0 bottom-0 z-50 w-[290px] overflow-y-auto" : "hidden lg:block"} lg:static lg:w-[260px] shrink-0`}
          style={open ? {
            background: "linear-gradient(180deg, rgba(5,7,13,0.98), rgba(8,8,16,0.98))",
            backdropFilter: "blur(20px)",
            borderRight: "1px solid rgba(34, 192, 115, 0.18)",
          } : undefined}
        >
          {open && (
            <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgba(34, 192, 115, 0.18)" }}>
              <div className="flex items-center gap-3">
                <div className="p-brand-mark" style={{ width: 38, height: 38, fontSize: 12 }}>M88</div>
                <div className="leading-tight">
                  <div className="text-[14px] font-black p-gold-text leading-none">Maxlive88</div>
                  <div className="text-[9px] uppercase tracking-[0.28em] text-white/45 font-semibold mt-1">Affiliate Suite</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg p-btn-ghost">
                <X size={16} />
              </button>
            </div>
          )}

          <div className={open ? "p-4" : ""}>
            <nav className="space-y-3">
              {NAV_GROUPS.map((group, gi) => (
                <div key={gi}>
                  {group.label && (
                    <div className="px-3 mb-1.5 text-[9.5px] font-black tracking-[0.32em] uppercase" style={{ color: "rgba(34, 192, 115, 0.55)" }}>
                      {group.label}
                    </div>
                  )}
                  <div className="space-y-1">
                    {group.items.map((it) => {
                      const Icon = it.icon;
                      return (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) => `p-nav-link ${isActive ? "active" : ""}`}
                          data-testid={`nav-${it.short.toLowerCase()}`}
                        >
                          <Icon size={16} />
                          <span className="flex-1">{it.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            <div className="mt-6 p-glass-light p-4 text-[11.5px] text-white/65 leading-relaxed">
              <div className="flex items-center gap-2 text-emerald-200 font-bold mb-1">
                <Headphones size={13} /> Concierge Support
              </div>
              Email&nbsp;
              <a className="text-emerald-300 underline decoration-amber-300/40 underline-offset-2" href="mailto:affiliate@maxlive88.com">
                affiliate@maxlive88.com
              </a>
              <div className="mt-1 text-white/40">24/7 dedicated affiliate desk</div>
            </div>
            <div className="mt-3 px-4 py-2.5 rounded-xl text-[10px] uppercase tracking-[0.25em] text-emerald-200/80 flex items-center gap-2" style={{
              background: "linear-gradient(90deg, rgba(34, 192, 115, 0.10), transparent)",
              border: "1px solid rgba(34, 192, 115, 0.18)",
            }}>
              <Sparkles size={12} /> Premium Edition
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-fade-up">
          <Outlet />
        </main>
      </div>
      <AnnouncementPopup api={api} />
    </div>
  );
}
